import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import AppLayout from "@/components/feature/AppLayout";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimeCard from "@/components/feature/AnimeCard";
import EmptyState from "@/components/feature/EmptyState";
import Pagination from "@/components/feature/Pagination";
import FilterPanel, { type FilterValue } from "@/components/feature/FilterPanel";
import FilterDrawer from "@/components/feature/FilterDrawer";
import { animeList } from "@/mocks/anime";
import { categories } from "@/mocks/categories";
import type { SortValue } from "@/mocks/filters";
import { PAGE_SIZE, sortAnime } from "@/utils/anime";
import { usePageMeta } from "@/hooks/usePageMeta";

const readFilter = (params: URLSearchParams, genre: string): FilterValue => ({
  genre,
  region: params.get("region") || "",
  year: params.get("year") || "",
  sort: (params.get("sort") as SortValue) || "popular",
  subgenre: params.get("subgenre") || "",
});

export default function Category() {
  const { categoryId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const category = categories.find((item) => item.id === categoryId);
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const genre = category?.name || "";
  const filter = useMemo(() => readFilter(searchParams, genre), [searchParams, genre]);
  const [draft, setDraft] = useState<FilterValue>(filter);

  useEffect(() => {
    setDraft(filter);
    setPage(1);
  }, [filter]);

  const categoryName = category ? `${category.name}动漫` : "动漫分类";
  usePageMeta(
    `${categoryName} - 动漫免费在观看`,
    category
      ? `${category.description.slice(0, 70)}在动漫免费在观看浏览${category.name}题材动漫作品，支持按地区、年份与评分筛选。`
      : "在动漫免费在观看浏览全部题材动漫作品，支持按地区、年份与评分筛选。",
    `${categoryName},${category?.name || "动漫"}在线观看,免费观看动漫,高清动漫,热门动漫`,
  );

  const genreAnime = useMemo(
    () => (category ? animeList.filter((item) => item.categories.includes(category.name)) : animeList),
    [category],
  );

  const filtered = useMemo(() => {
    const list = genreAnime.filter((anime) => {
      if (filter.region && anime.region !== filter.region) return false;
      if (filter.year && String(anime.year) !== filter.year) return false;
      if (filter.subgenre && !anime.categories.includes(filter.subgenre)) return false;
      return true;
    });
    return sortAnime(list, filter.sort);
  }, [genreAnime, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const applyFilter = (next: FilterValue) => {
    const params = new URLSearchParams();
    if (next.region) params.set("region", next.region);
    if (next.year) params.set("year", next.year);
    if (next.sort) params.set("sort", next.sort);
    if (next.subgenre) params.set("subgenre", next.subgenre);
    setSearchParams(params, { replace: true });
    setPage(1);
    setDrawerOpen(false);
  };

  const resetFilter = () => {
    setSearchParams(new URLSearchParams(), { replace: true });
    setPage(1);
  };

  const activeCount = [filter.region, filter.year, filter.subgenre].filter(Boolean).length;

  return (
    <AppLayout>
      <div className="px-4 md:px-6 pt-5">
        <Breadcrumb items={[{ label: "分类", to: "/list" }, { label: categoryName }]} />

        <header className="mt-3 rounded-lg border border-background-200 bg-background-100 px-5 py-5 md:px-7 md:py-6">
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            <span className="w-12 h-12 shrink-0 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
              <i className={`${category?.icon || "ri-apps-2-line"} text-2xl`} />
            </span>
            <div className="min-w-0 flex-1">
              <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{categoryName}</h1>
              <p className="mt-1.5 text-[13px] leading-relaxed text-foreground-600 max-w-3xl">
                {category?.description || "浏览站内收录的全部题材动漫作品。"}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-foreground-600">
                <span className="px-2 py-1 rounded-md bg-background-50 border border-background-200">
                  收录 <strong className="text-foreground-900">{genreAnime.length}</strong> 部
                </span>
                <span className="px-2 py-1 rounded-md bg-background-50 border border-background-200">
                  当前筛选 <strong className="text-foreground-900">{filtered.length}</strong> 部
                </span>
                {category?.subGenres.map((sub) => (
                  <button
                    key={sub}
                    type="button"
                    onClick={() => applyFilter({ ...filter, subgenre: filter.subgenre === sub ? "" : sub })}
                    className={`px-2.5 py-1 rounded-md border transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                      filter.subgenre === sub
                        ? "bg-accent-500 border-accent-500 text-background-50"
                        : "bg-background-50 border-background-200 hover:border-accent-300 text-foreground-700"
                    }`}
                  >
                    <i className="ri-price-tag-3-line text-[11px]" />
                    {sub}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </header>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[230px_1fr] gap-6">
          <aside className="hidden lg:block">
            <div className="sticky top-6 rounded-lg border border-background-200 bg-background-100 p-4">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-heading text-sm font-bold text-foreground-950">筛选</h2>
                <button
                  type="button"
                  onClick={resetFilter}
                  className="text-[11px] text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  重置
                </button>
              </div>
              <FilterPanel value={filter} onChange={applyFilter} hideGenre layout="sidebar" />
              <div className="mt-5 pt-4 border-t border-background-200">
                <p className="text-[11px] font-semibold tracking-wider text-foreground-500 mb-2">其他分类</p>
                <div className="flex flex-wrap gap-1.5">
                  {categories
                    .filter((cat) => cat.id !== categoryId)
                    .map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.id}`}
                        className="px-2 py-1 rounded-md bg-background-50 border border-background-200 text-[11px] text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {cat.name}
                      </Link>
                    ))}
                </div>
              </div>
            </div>
          </aside>

          <section>
            <div className="flex items-center justify-between gap-2 mb-4">
              <p className="text-[12px] text-foreground-600">
                共找到 <strong className="text-foreground-900">{filtered.length}</strong> 部作品
                {filter.region ? ` · 地区：${filter.region}` : ""}
                {filter.year ? ` · 年份：${filter.year}` : ""}
              </p>
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="lg:hidden h-8 px-3 rounded-md border border-background-300 bg-background-50 text-[12px] text-foreground-800 flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-filter-3-line" />
                筛选
                {activeCount ? (
                  <span className="ml-0.5 min-w-[16px] h-4 px-1 rounded-full bg-accent-500 text-background-50 text-[10px] leading-4">
                    {activeCount}
                  </span>
                ) : null}
              </button>
            </div>

            {visible.length ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-x-3 gap-y-5">
                  {visible.map((anime) => (
                    <AnimeCard key={anime.id} anime={anime} />
                  ))}
                </div>
                <Pagination page={page} totalPages={totalPages} onChange={setPage} />
              </>
            ) : (
              <EmptyState
                title="这个组合下暂时没有作品"
                description="试着放宽年份或地区条件，或者查看该分类下的全部作品。"
                onReset={resetFilter}
                resetLabel="重置筛选条件"
              >
                <Link
                  to="/list"
                  className="mt-2 text-[12px] text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  前往全部动漫筛选页
                </Link>
              </EmptyState>
            )}
          </section>
        </div>
      </div>
      <FilterDrawer
        open={drawerOpen}
        value={draft}
        onChange={setDraft}
        onClose={() => setDrawerOpen(false)}
        onApply={applyFilter}
        onReset={() => {
          setDraft({ ...filter, region: "", year: "", subgenre: "" });
        }}
        showGenre={false}
      />
      <SiteFooter />
    </AppLayout>
  );
}